import { useEffect, useRef } from "react";

import type { Locale } from "../../../shared/types";
import { acquireBlob } from "../blob/renderer";
import { useCopy } from "../lib/copy";

/**
 * The two screens that frame the product: the one before an account exists, and
 * the one immediately after it does.
 *
 * They live in one file because they are the same object — a single viewport that
 * says what this is, in the consultant's voice, with one way forward and nothing
 * that scrolls. Everything about them is measured against that: no card, no
 * illustration, no scroll, no third paragraph. `docs/DESIGN.md` 8 and the
 * one-screen requirement in `docs/SECURITY-W4.md` §5.2 are the binding versions.
 *
 * `Landing` is content only. It renders INSIDE `Auth`'s stage rather than owning
 * a screen of its own, because the blob, the top bar and the status line must
 * survive the move from the landing to the form — a remount there destroys the
 * WebGL context and recompiles the shader at exactly the moment somebody is
 * waiting on a sign-in.
 *
 * `Welcome` does own its screen: it renders between the auth screen and the
 * conversation, where a remount already happens either way.
 */

interface LandingProps {
  locale: Locale;
  /**
   * From GET /api/config. "private" means the door is shut — no create-account
   * anywhere, and the one quiet sentence saying so.
   */
  access: "private" | "open";
  /** The primary action. Sign-up when the door is open, sign-in when it is not. */
  onBegin: () => void;
  /** The second way in, offered only when the first one leads somewhere else. */
  onSignIn: () => void;
}

export default function Landing({ locale, access, onBegin, onSignIn }: LandingProps) {
  const copy = useCopy(locale);
  // When nobody can create an account, "Begin" and "I already have an account"
  // are the same button pointing at the same form, and offering both is a lie
  // about there being a choice.
  const canSignUp = access === "open";

  return (
    <>
      <h1 className="landing__name">{copy.landingName}</h1>
      <p className="landing__what">{copy.landingWhat}</p>
      <p className="landing__what">{copy.landingLeave}</p>
      <div className="landing__go">
        <button className="btn btn--primary landing__cta" type="button" onClick={onBegin}>
          {copy.landingCta}
        </button>
        {/* The slot is reserved whether or not it holds a button, for the same
            reason the access line below is: the config answer arrives a beat
            after the first paint, and a button that appears under the primary
            action must not move the primary action. */}
        <p className="landing__alt">
          {canSignUp ? (
            <button className="btn btn--text" type="button" onClick={onSignIn}>
              {copy.landingSignIn}
            </button>
          ) : (
            " "
          )}
        </p>
      </div>
      <p className="landing__access label">{canSignUp ? " " : copy.landingPrivate}</p>
    </>
  );
}

interface WelcomeProps {
  locale: Locale;
  /** Into the intake. */
  onStart: () => void;
  /**
   * Past it, to the same place. The screen is a courtesy, not a gate: a person
   * who has done this before, or who simply does not want to be told, must not
   * have to read it to reach their conversation.
   */
  onSkip: () => void;
  onChangeLocale: (locale: Locale) => void;
}

/** Endonyms, as everywhere else: the other language names itself. */
const OTHER_LANGUAGE: Record<Locale, string> = { en: "Русский", ru: "English" };

/**
 * The one honest moment a brand-new account gets before a stranger starts asking
 * about their business.
 *
 * What it is for, precisely: `docs/UX-AUDIT.md` C1 measured a new account landing
 * straight in an intake interview with nothing naming the product, nothing saying
 * how long this takes, nothing saying you may talk instead of type, and nothing
 * saying you may stop. Three sentences fix all four, and the fourth thing it does
 * is the important one — it says you can leave.
 *
 * What it is not: a tour, a checklist, a carousel, a modal, or a page that
 * congratulates anybody for signing up. It is skippable and it never returns.
 */
export function Welcome({ locale, onStart, onSkip, onChangeLocale }: WelcomeProps) {
  const copy = useCopy(locale);
  const blobRef = useRef<HTMLDivElement | null>(null);
  const startRef = useRef<HTMLButtonElement | null>(null);

  // The same shared canvas the auth screen and the conversation use, borrowed
  // rather than built.
  useEffect(() => {
    const host = blobRef.current;
    if (!host) return;
    const { canvas, renderer } = acquireBlob();
    host.appendChild(canvas);
    renderer.start();
    renderer.setState("idle");
    return () => {
      renderer.stop();
      renderer.setLevel(0);
      canvas.remove();
    };
  }, []);

  // The keyboard lands on the way forward, so Return works without a hunt and
  // the first Tab reaches the way past.
  useEffect(() => {
    startRef.current?.focus();
  }, []);

  return (
    <div className="field field--auth field--landing">
      <header className="topbar">
        <div className="topbar__slot" aria-hidden="true" />
        <div className="topbar__spacer" />
        <button
          className="btn btn--text"
          type="button"
          onClick={() => onChangeLocale(locale === "en" ? "ru" : "en")}
        >
          {OTHER_LANGUAGE[locale]}
        </button>
      </header>

      <main className="stage">
        <div className="blob" aria-hidden="true" ref={blobRef} />

        <h1 className="landing__name">{copy.welcomeTitle}</h1>
        <p className="landing__what">{copy.welcomeWhat}</p>
        <p className="landing__what">{copy.welcomeHow}</p>
        <p className="landing__what">{copy.welcomeStop}</p>

        <div className="landing__go">
          <button
            className="btn btn--primary landing__cta"
            type="button"
            ref={startRef}
            onClick={onStart}
          >
            {copy.welcomeCta}
          </button>
          <p className="landing__alt">
            <button className="btn btn--text" type="button" onClick={onSkip}>
              {copy.welcomeSkip}
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
